/**
 * HolidayCard — renders one holiday using the right UI pattern (A/B/C).
 *
 * Spec: AWENI/docs/HIJRI-UNCERTAINTY-UX.md
 *
 * Pattern A — far-future (>30 days)        — single date + "±1 day" badge
 * Pattern B — approaching (≤30 days)       — both dates + country hint
 * Pattern C — confirmed                    — country-by-country list
 *
 * Pattern D (calendar grid badge) is rendered by Yawmi when its calendar
 * grid ships (C6) — not in this widget.
 *
 * Pure render function — no state, no I/O. Used by Hisni preview and by
 * future Yawmi/Holiday widgets.
 */

import type { AweniContext } from '../../../types';
import type { NextHolidayResult, PerYearOccurrence } from '../../../services/HijriCalendar';

export interface HolidayCardProps {
  result: NextHolidayResult;
  ctx: AweniContext;
  /** ISO 3166-1 alpha-2 of the user — used for the "your country" line. */
  country: string;
  /** Optional callback for the "Notify me when confirmed" button (Pattern B). */
  onNotifyClick?: () => void;
  /** Optional callback for the "Add to calendar" button (Pattern C). */
  onAddCalendarClick?: () => void;
}

function escHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

function formatDateLong(iso: string, locale: string): string {
  try {
    const d = new Date(iso + 'T12:00:00Z');
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
}

function countdownLabel(daysAway: number, t: (k: string) => string): string {
  if (daysAway === 0) return t('holiday_today') || 'TODAY';
  if (daysAway === 1) return t('holiday_tomorrow') || 'TOMORROW';
  return `${t('holiday_in') || 'in'} ${daysAway} ${t('holiday_days') || 'days'}`;
}

function renderPatternA(p: HolidayCardProps): string {
  const { result, ctx } = p;
  const t = (k: string) => ctx.i18n.t(k);
  const locale = ctx.i18n.locale;
  const name = result.holiday.name[locale] || t(`holiday_${result.holiday.id}`) || result.holiday.id;
  const eff = formatDateLong(result.effectiveDate, locale);
  const sighting = result.occurrence.moonSightingExpected
    ? t('holiday_sighting_expected').replace('{date}', formatDateLong(result.occurrence.moonSightingExpected.slice(0, 10), locale))
    : '';
  const badge = result.holiday.uncertaintyKind === 'moon_sighting' ? `
    <span class="ln-uncertainty-badge"
          aria-label="${escHtml(t('holiday_uncertainty_aria_pm1') || 'Date may shift by one day depending on moon sighting')}">
      ${escHtml(t('holiday_uncertainty_badge_pm1') || '±1 day')}
    </span>
  ` : '';
  return `
    <article class="ln-holiday-card" data-pattern="A_far_future" data-holiday-id="${escHtml(result.holiday.id)}">
      <header class="ln-holiday-card__header">
        <span class="ln-holiday-icon" aria-hidden="true">🕌</span>
        <h3>${escHtml(name)}</h3>
      </header>
      <p class="ln-holiday-hijri">${escHtml(`${result.holiday.hijriDate.day} · ${result.holiday.hijriDate.month} · H`)}</p>
      <p class="ln-holiday-gregorian-approx">
        ≈ <time datetime="${escHtml(result.effectiveDate)}">${escHtml(eff)}</time>
        ${badge}
      </p>
      ${sighting ? `<p class="ln-sighting-note">${escHtml(sighting)}</p>` : ''}
    </article>
  `;
}

function renderPatternB(p: HolidayCardProps): string {
  const { result, ctx, country } = p;
  const t = (k: string) => ctx.i18n.t(k);
  const locale = ctx.i18n.locale;
  const name = result.holiday.name[locale] || t(`holiday_${result.holiday.id}`) || result.holiday.id;
  const cnt = countdownLabel(result.daysAway, t);
  const primary = result.occurrence.primary;
  const alternate = result.occurrence.alternate || '';
  const tendency = (result.effectiveDate === alternate)
    ? (t('holiday_country_typical_pattern') || 'Your country ({country}): typically follows national sighting → likely {date}')
        .replace('{country}', country).replace('{date}', formatDateLong(alternate, locale))
    : '';
  return `
    <article class="ln-holiday-card" data-pattern="B_approaching" data-holiday-id="${escHtml(result.holiday.id)}">
      <header class="ln-holiday-card__header">
        <span class="ln-holiday-icon" aria-hidden="true">🕌</span>
        <h3>${escHtml(name)} <span class="ln-countdown">· ${escHtml(cnt)}</span></h3>
      </header>
      <dl class="ln-uncertainty-dates">
        <dt>${escHtml(t('holiday_most_likely') || 'Most likely')}</dt>
        <dd><time datetime="${escHtml(primary)}">${escHtml(formatDateLong(primary, locale))}</time></dd>
        ${alternate ? `
          <dt>${escHtml(t('holiday_alternate') || 'Alternate')}</dt>
          <dd><time datetime="${escHtml(alternate)}">${escHtml(formatDateLong(alternate, locale))}</time></dd>
        ` : ''}
      </dl>
      ${tendency ? `<hr class="ln-divider" /><p class="ln-country-hint">${escHtml(tendency)}</p>` : ''}
      <button type="button" class="ln-btn ln-btn-secondary"
              data-action="notify-on-confirm" data-holiday-id="${escHtml(result.holiday.id)}">
        ${escHtml(t('holiday_notify_when_confirmed') || '🔔 Notify me when confirmed')}
      </button>
    </article>
  `;
}

function renderPatternC(p: HolidayCardProps): string {
  const { result, ctx, country } = p;
  const t = (k: string) => ctx.i18n.t(k);
  const locale = ctx.i18n.locale;
  const name = result.holiday.name[locale] || t(`holiday_${result.holiday.id}`) || result.holiday.id;
  const cnt = countdownLabel(result.daysAway, t);

  // Group countries by date from perCountry — populated by the post-Maghrib
  // sighting watcher once it has confirmed per-country announcements.
  const byDate: Record<string, string[]> = {};
  const perCountry = result.occurrence.perCountry || {};
  for (const c of Object.keys(perCountry) as Array<keyof typeof perCountry>) {
    const date = perCountry[c];
    if (!date) continue;
    (byDate[date] ||= []).push(String(c));
  }
  const dates = Object.keys(byDate).sort();
  const hasPerCountry = dates.length > 0;

  // When the watcher hasn't populated perCountry (most common path right now,
  // because the cron is queued but not yet live), the date is still
  // deterministically confirmed by the algorithmic collapse against
  // Intl islamic-umalqura — we just don't have multi-country attestation.
  // Render a single, precise "Confirmed: <date>" line. Never say "Most likely".
  if (!hasPerCountry) {
    const confirmedLine = (t('holiday_confirmed_date') || 'Confirmed: {date}')
      .replace('{date}', formatDateLong(result.effectiveDate, locale));
    return `
      <article class="ln-holiday-card" data-pattern="C_confirmed" data-confirmed-source="algorithmic" data-holiday-id="${escHtml(result.holiday.id)}">
        <header class="ln-holiday-card__header">
          <span class="ln-holiday-icon" aria-hidden="true">🕌</span>
          <h3>${escHtml(name)} <span class="ln-countdown ln-countdown-imminent">· ${escHtml(cnt)}</span></h3>
        </header>
        <p class="ln-confirmed-single">
          <span class="ln-checkmark" aria-hidden="true">✅</span>
          <time datetime="${escHtml(result.effectiveDate)}">${escHtml(confirmedLine)}</time>
        </p>
      </article>
    `;
  }

  // Multi-country sighting attestation path — watcher has populated perCountry.
  const yourLine = (t('holiday_your_country_confirmed') || 'Your country ({country}): {date}')
    .replace('{country}', country)
    .replace('{date}', formatDateLong(result.effectiveDate, locale));
  return `
    <article class="ln-holiday-card" data-pattern="C_confirmed" data-confirmed-source="sighting" data-holiday-id="${escHtml(result.holiday.id)}">
      <header class="ln-holiday-card__header">
        <span class="ln-holiday-icon" aria-hidden="true">🕌</span>
        <h3>${escHtml(name)} <span class="ln-countdown ln-countdown-imminent">· ${escHtml(cnt)}</span></h3>
      </header>
      <ul class="ln-confirmed-dates">
        ${dates.map((d) => `
          <li>
            <span class="ln-checkmark" aria-hidden="true">✅</span>
            <time datetime="${escHtml(d)}">${escHtml(formatDateLong(d, locale))}</time>
            — <span class="ln-countries">${escHtml(byDate[d].join(', '))}</span>
          </li>
        `).join('')}
      </ul>
      <p class="ln-your-country">${escHtml(yourLine)}</p>
    </article>
  `;
}

export function renderHolidayCard(props: HolidayCardProps): string {
  switch (props.result.uiPattern) {
    case 'A_far_future': return renderPatternA(props);
    case 'B_approaching': return renderPatternB(props);
    case 'C_confirmed': return renderPatternC(props);
    case 'D_grid_cell':
    default: return renderPatternA(props);
  }
}

// Exported for tests
export const __TEST_RENDERERS__ = { renderPatternA, renderPatternB, renderPatternC };

// Touch unused params symbol so TS strict mode doesn't complain
export type { PerYearOccurrence };
